import { useEffect, useRef, useState } from 'react';
import * as db from '../lib/data.js';

// ไฟล์อ้างอิงของสินค้า (deploy-37) — แสดงในหน้าแก้ไขสินค้า
//
// ทำไมมี: user อยากเก็บไฟล์รายละเอียดสินค้าไว้ดู "เป็น reference เฉย ๆ" เช่น สเปคชีต ·
//   หนังสือที่ซัพยืนยันวัสดุเป็นลายลักษณ์อักษร · ผลทดสอบ — เดิมไม่มีที่เก็บ (มีแต่ในหน้าใบอนุญาตซึ่งไม่มีใครเจอ
//   และอัปเข้าที่เก็บสาธารณะ)
//
// กติกา:
//   · ไฟล์อยู่ bucket ส่วนตัว เปิดด้วยลิงก์ชั่วคราว — เปิดแท็บทันทีตอนคลิกแล้วค่อยใส่ลิงก์ (กันเบราว์เซอร์บล็อก popup)
//   · ไฟล์จากหน้านี้ = kind 'reference' และ **ไม่ใส่ซองยื่นหน่วยงาน** จนกว่าจะมีคนไปติ๊กในหน้าใบอนุญาตเอง
//   · โหลดเฉพาะตอนเปิดใบนี้ — หน้ารวมสินค้าไม่แตะตารางนี้เลย จึงไม่ทำให้หน้ารวมช้าลง
//   · เอาออก = ลบแถว (เข้าถังขยะ) ตัวไฟล์ใน storage ยังอยู่ → กู้คืนได้
//   · ปุ่มแนบเปิดเมื่อโหลดรายการสำเร็จแล้วเท่านั้น — ถ้ายังไม่รัน deploy-37 การอัปจะทิ้งไฟล์กำพร้าไว้ใน storage
//     ที่ไม่มีใครเห็นและลบจากหน้าเว็บไม่ได้ (รีวิว 26 ต.ค.)

const DocIcon = () => (
  <svg viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" aria-hidden="true">
    <path d="M13 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M13 3v5h5"/>
  </svg>
);

const fmtDay = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('th-TH',
      { timeZone: 'Asia/Bangkok', day: 'numeric', month: 'short', year: '2-digit' });
  } catch { return ''; }
};

// ไฟล์ที่แนบจากหน้าเอกสารยื่นขอใบอนุญาตจะมีหมวด — โชว์ให้รู้ว่ามาจากไหน
const KIND_LABEL = {
  catalog: 'แคตตาล็อก', spec: 'สเปค', manual: 'คู่มือ', test: 'ผลทดสอบ',
  license: 'ใบอนุญาต/หนังสือรับรอง', other: 'เอกสารอื่น',
};

// ตาราง product_file ยังไม่มี (ยังไม่รัน deploy-37) — PostgREST ตอบ PGRST205 / Postgres 42P01
const tableMissing = (e) => e?.code === 'PGRST205' || e?.code === '42P01'
  || /ib_product_file|product_file.*(not exist|find)/i.test(e?.message ?? '');

export default function ProductFiles({ classId, onBusy }) {
  const [files, setFiles] = useState([]);
  const [state, setState] = useState('loading');   // loading | ready | missing | error
  const [busy, setBusyState] = useState(null);     // ข้อความระหว่างอัป เช่น "กำลังแนบ 2/3…"
  const [msg, setMsg] = useState(null);            // { kind: 'err' | 'ok', text }
  const [confirmId, setConfirmId] = useState(null);
  const [drag, setDrag] = useState(false);
  const input = useRef(null);
  const alive = useRef(true);

  const setBusy = (v) => { if (alive.current) setBusyState(v); onBusy?.(!!v); };

  useEffect(() => {
    alive.current = true;
    setState('loading');
    db.listProductFiles(classId)
      .then(r => { if (alive.current) { setFiles(r); setState('ready'); } })
      .catch(e => {
        if (!alive.current) return;
        if (tableMissing(e)) { setState('missing'); return; }
        setState('error');
        setMsg({ kind: 'err', text: `โหลดรายการไฟล์ไม่สำเร็จ: ${e.message ?? String(e)}` });
      });
    return () => { alive.current = false; onBusy?.(false); };
  }, [classId]);

  // ไฟล์ที่ลากมาวาง "นอกกรอบ" — เบราว์เซอร์จะเปิดไฟล์นั้นแทนหน้าแอพ แล้วสิ่งที่กรอกค้างไว้ในใบนี้หายหมด
  // กันไว้ตลอดที่ส่วนนี้เปิดอยู่ (ไม่อัปไฟล์ แค่ไม่ให้หน้าเปลี่ยน)
  useEffect(() => {
    const stop = (e) => { if (e.dataTransfer?.types?.includes('Files')) e.preventDefault(); };
    window.addEventListener('dragover', stop);
    window.addEventListener('drop', stop);
    return () => { window.removeEventListener('dragover', stop); window.removeEventListener('drop', stop); };
  }, []);

  const upload = async (list) => {
    const arr = [...(list ?? [])];
    if (!arr.length || state !== 'ready') return;
    setMsg(null);
    const failed = [];
    for (let i = 0; i < arr.length; i++) {
      setBusy(arr.length > 1 ? `กำลังแนบ ${i + 1}/${arr.length}…` : 'กำลังแนบ…');
      try {
        const row = await db.uploadProductFile(classId, arr[i]);
        if (row && alive.current) setFiles(prev => [row, ...prev]);
      } catch (e) {
        failed.push(`${arr[i].name}: ${e.message ?? String(e)}`);
      }
    }
    setBusy(null);
    if (failed.length && alive.current) {
      setMsg({ kind: 'err', text: `แนบไม่สำเร็จ ${failed.length} ไฟล์ — ${failed.join(' · ')}` });
    }
  };

  const open = (f) => {
    const w = window.open('', '_blank');
    if (w) w.opener = null;   // แท็บที่เปิดไฟล์ต้องสั่งหน้าแอพนี้ไม่ได้
    db.productFileOpenUrl(f)
      .then(url => { if (w) w.location.href = url; else window.open(url, '_blank', 'noopener'); })
      .catch(e => { if (w) w.close(); setMsg({ kind: 'err', text: `เปิดไฟล์ไม่สำเร็จ: ${e.message ?? String(e)}` }); });
  };

  const saveNote = async (f, value) => {
    const v = (value || '').trim();
    if (v === (f.note || '')) return;
    try {
      const row = await db.updateProductFile(f.id, { note: v });
      if (alive.current) setFiles(prev => prev.map(x => (x.id === f.id ? row : x)));
    } catch (e) { setMsg({ kind: 'err', text: `บันทึกหมายเหตุไม่สำเร็จ: ${e.message ?? String(e)}` }); }
  };

  const remove = async (f) => {
    setConfirmId(null);
    try {
      await db.removeProductFile(f.id);
      setFiles(prev => prev.filter(x => x.id !== f.id));
      setMsg({ kind: 'ok', text: `เอา "${f.file_name}" ออกแล้ว — ถ้าเอาออกผิด แอดมินกู้คืนได้ที่ ตั้งค่า › ถังขยะ` });
    } catch (e) { setMsg({ kind: 'err', text: `เอาไฟล์ออกไม่สำเร็จ: ${e.message ?? String(e)}` }); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    if (state !== 'ready') return;
    if (busy) { setMsg({ kind: 'err', text: 'กำลังแนบไฟล์ก่อนหน้าอยู่ — วางไฟล์นี้อีกครั้งเมื่อเสร็จได้เลย' }); return; }
    upload(e.dataTransfer?.files);
  };

  // ยังไม่รัน deploy-37 = ไม่โชว์อะไรเลย (หน้าเว็บขึ้นก่อน SQL ได้โดยพนักงานไม่เห็นข้อความเทคนิค)
  //   พอรัน SQL แล้ว ส่วนนี้โผล่เองโดยไม่ต้อง deploy ใหม่
  if (state === 'missing') return null;

  return (
    <div className={`prf${drag ? ' drag' : ''}`}
      onDragOver={e => { e.preventDefault(); if (!drag && state === 'ready') setDrag(true); }}
      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDrag(false); }}
      onDrop={onDrop}>
      <input ref={input} type="file" multiple accept={db.PRODUCT_FILE_ACCEPT} style={{ display: 'none' }}
        onChange={e => { const l = e.target.files; upload(l).finally(() => { e.target.value = ''; }); }}/>

      <div className="prf-h">
        <span className="prf-t">มีไฟล์รายละเอียดสินค้าไหม? <span className="prf-opt">(ถ้ามี)</span></span>
        <button type="button" className="btn2 sm" disabled={!!busy || state !== 'ready'}
          onClick={() => input.current?.click()}>
          {busy || '＋ แนบไฟล์'}</button>
      </div>
      <div className="fhint">
        เช่น สเปคชีต · หนังสือที่ซัพยืนยันวัสดุ · ผลทดสอบ — ไฟล์ที่แนบตรงนี้<b>เก็บไว้ดูภายในทีม ไม่ถูกส่งไปไหน</b>
        {' '}จนกว่าจะติ๊ก “ใส่ในซองยื่น” ในหน้าเอกสารยื่นขอใบอนุญาต · ลากไฟล์มาวางในกรอบนี้ได้ ·
        ไฟล์ละไม่เกิน {db.fmtBytes(db.PRODUCT_FILE_MAX)}
      </div>

      {msg && <div className={msg.kind === 'err' ? 'ferr' : 'gap-msg'}>{msg.text}</div>}

      {state === 'loading' ? <div className="fhint">กำลังโหลดรายการไฟล์…</div>
        : files.length === 0 ? null
        : (
          <ul className="prf-list">
            {files.map(f => (
              <li key={f.id} className="prf-row">
                <button type="button" className="prf-ic" onClick={() => open(f)} title="เปิดไฟล์"><DocIcon/></button>
                <div className="prf-main">
                  <button type="button" className="prf-nm" onClick={() => open(f)} title={f.file_name}>{f.file_name}</button>
                  <div className="prf-meta">
                    {[db.fmtBytes(f.size_bytes), fmtDay(f.uploaded_at)].filter(Boolean).join(' · ')}
                    {KIND_LABEL[f.kind] && <span className="prf-tag kind">{KIND_LABEL[f.kind]}</span>}
                    {f.for_permit && (
                      <span className="prf-tag" title="ติ๊กไว้ในหน้าเอกสารยื่นขอใบอนุญาต — ไฟล์นี้จะอยู่ในซองที่ยื่นหน่วยงาน">
                        อยู่ในซองยื่นหน่วยงาน</span>
                    )}
                  </div>
                  <input className="prf-note" defaultValue={f.note ?? ''} maxLength={300}
                    placeholder="บันทึกสั้น ๆ ว่าไฟล์นี้คืออะไร (ไม่บังคับ) เช่น ซัพยืนยันวัสดุทางอีเมล 12 ต.ค."
                    onBlur={e => saveNote(f, e.target.value)}
                    // อยู่ใน <form> ของหน้าแก้ไขสินค้า — Enter ปกติจะกด "บันทึก" ทั้งใบ → ให้แค่จบการพิมพ์
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}/>
                </div>
                {confirmId === f.id ? (
                  <span className="prf-confirm">
                    {f.for_permit ? 'ไฟล์นี้อยู่ในซองยื่นด้วย — เอาออกแล้วจะหายจากซองด้วย เอาออกไหม?' : 'เอาไฟล์นี้ออก?'}
                    <button type="button" className="btn-del" onClick={() => remove(f)}>เอาออก</button>
                    <button type="button" className="btn2 sm" onClick={() => setConfirmId(null)}>ไม่เอาออก</button>
                  </span>
                ) : (
                  <button type="button" className="prf-x" onClick={() => setConfirmId(f.id)} title="เอาไฟล์นี้ออก">เอาออก</button>
                )}
              </li>
            ))}
          </ul>
        )}
    </div>
  );
}
